// taskList.js
import React, { useState, useEffect } from "react";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";

import { Accordion, AccordionTab } from "primereact/accordion";
import Task from "../task/task";
import { Calendar } from "primereact/calendar";
import { CREATE_TASK_URL, DELETE_TASK_URL } from "../../global/API";
import Cookies from "js-cookie";
import Swal from "sweetalert2";

export default function TaskList(props) {
  const { taskList, modifiedTaskDetails, setModifiedTaskDetails, onClickTaskDetail } = props;
  const [tasksState, setTasksState] = useState({});
  const [showTaskForm, setShowTaskForm] = useState(null);
  // const [dates, setDates] = useState(null);

  const [collapsed, setCollapsed] = useState(() => {
    const collapsedParents = {};
    return collapsedParents;
  });
  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    if (!taskList?.statusList) return;
    const initial = {};
    taskList.statusList.forEach((s) => {
      const key = s.task_status.replace(/\s+/g, "");
      initial[key] = taskList[key] && taskList[key].data ? [...taskList[key].data] : [];
    });
    setTasksState(initial);
  }, [taskList]);

  const toggleCollapse = (taskId) => {
    setCollapsed((prev) => {
      const current = prev[taskId] ?? true; // default collapsed
      return {
        ...prev,
        [taskId]: !current,
      };
    });
  };

  const buildVisibleTasks = (allTasks, parent = 0, depth = 0) => {
    let result = [];

    allTasks
      .filter((t) => t.parent === parent)
      .forEach((task) => {
        result.push({ ...task, depth });
        const isCollapsed = collapsed[task.id] ?? true; // default collapsed
        if (!isCollapsed) {
          result = result.concat(buildVisibleTasks(allTasks, task.id, depth + 1));
        }
      });

    return result;
  };

  const onHandleEvent = (event, setFunction) => {
    const { name, value } = event.target;
    setFunction((previousState) => ({ ...previousState, [name]: value }));
  };

  const onHandleSubmit = async (e, status) => {
    e.preventDefault();

    const formData = new FormData();

    for (const key in modifiedTaskDetails) {
      formData.append(key, modifiedTaskDetails[key]);
    }
    formData.append("parent", 0);
    formData.append("assignee", 0);
    formData.append("task_status", status);
    // formData.append("dueDate", modifiedTaskDetails.dueDate ? modifiedTaskDetails.dueDate.toISOString().split("T")[0] : "");

    try {
      const jwtToken = Cookies.get("jwtToken");
      const options = {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          Accept: "application/json",
        },
      };

      const response = await fetch(CREATE_TASK_URL + "0", options);
      const data = await response.json();

      if (data.status === "Success") {
        setShowTaskForm(null);
        setModifiedTaskDetails({});
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleUpdate = async (id, updatedFields, prevStatus) => {
    const jwtToken = Cookies.get("jwtToken");

    // helper to convert status label -> statusKey (remove spaces)
    const labelToKey = (label) => (label ? String(label).replace(/\s+/g, "") : "");

    setTasksState((prev) => {
      // copy
      const newState = { ...prev };
      // find the bucket containing the task if prevStatus not provided
      let prevKey = prevStatus ? labelToKey(prevStatus) : null;

      if (!prevKey) {
        // try to locate the task
        for (const k of Object.keys(prev)) {
          if (prev[k].some((t) => String(t.id) === String(id))) {
            prevKey = k;
            break;
          }
        }
      }

      // clone prev bucket lists
      Object.keys(newState).forEach((k) => {
        newState[k] = [...(prev[k] || [])];
      });

      // find the task object (may be in prevKey)
      let taskObj = null;
      if (prevKey && newState[prevKey]) {
        const idx = newState[prevKey].findIndex((t) => String(t.id) === String(id));
        if (idx !== -1) {
          taskObj = { ...newState[prevKey][idx], ...updatedFields };
          // If status is changing to a different key, remove from prev bucket for now (we'll insert into new)
          if (updatedFields.task_status) {
            newState[prevKey].splice(idx, 1);
          } else {
            // update in place
            newState[prevKey][idx] = taskObj;
            return newState;
          }
        }
      }

      // If we didn't find it in prevKey, search all buckets and update in place if no status change:
      if (!taskObj && !updatedFields.task_status) {
        for (const k of Object.keys(newState)) {
          const idx = newState[k].findIndex((t) => String(t.id) === String(id));
          if (idx !== -1) {
            taskObj = { ...newState[k][idx], ...updatedFields };
            newState[k][idx] = taskObj;
            return newState;
          }
        }
      }

      // If status changed, insert task into the new status bucket
      if (updatedFields.task_status) {
        const newKey = labelToKey(updatedFields.task_status);
        // ensure bucket exists
        if (!newState[newKey]) newState[newKey] = [];
        // Insert at top (or you can push to end)
        // maintain minimal required fields: keep original object if we have it, else create new
        const insertionObj = taskObj || { id, ...updatedFields };
        // if the task object doesn't have other fields (title etc.), attempt to copy from any bucket
        if (!taskObj) {
          for (const k of Object.keys(prev)) {
            const found = prev[k].find((t) => String(t.id) === String(id));
            if (found) {
              Object.assign(insertionObj, found, updatedFields);
              break;
            }
          }
        }
        newState[newKey] = [insertionObj, ...newState[newKey]];
      }

      return newState;
    });

    // Call backend (optimistic)
    try {
      const options = {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(updatedFields),
      };

      const response = await fetch(CREATE_TASK_URL + id, options);
      if (!response.ok) {
        console.error("Failed to update task:", await response.text());
        Swal.fire({ title: "Error", text: "Failed to save changes.", icon: "error" });
        // Optionally: re-fetch tasks or revert optimistic change here
      }
    } catch (err) {
      console.error("Error updating task:", err);
      Swal.fire({ title: "Error", text: "Error saving changes.", icon: "error" });
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const jwtToken = Cookies.get("jwtToken");
        const options = {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            Accept: "application/json",
          },
        };

        try {
          const response = await fetch(DELETE_TASK_URL + id, options);
          if (response.ok) {
            // remove the task locally from tasksState so UI updates immediately
            setTasksState((prev) => {
              const updated = {};
              Object.keys(prev).forEach((k) => {
                updated[k] = prev[k].filter((t) => String(t.id) !== String(id));
              });
              return updated;
            });

            // Swal.fire({
            //   title: "Deleted!",
            //   text: "Task has been deleted.",
            //   icon: "success",
            // });
          } else {
            console.error("Failed to delete task");
            Swal.fire({
              title: "Error",
              text: "Failed to delete task.",
              icon: "error",
            });
          }
        } catch (error) {
          console.error("Error deleting task:", error);
          Swal.fire({
            title: "Error",
            text: "Error deleting task.",
            icon: "error",
          });
        }
      }
    });
  };

  const getStatusClass = (status) => {
    return status.toLowerCase().replace(/\s+/g, "-");
  };

  return (
    <>
      {taskList?.statusList ? (
        <div>
          <Accordion multiple activeIndex={[0]}>
            {taskList.statusList.map((status, index) => {
              const { task_status, count } = status;
              const statusKey = task_status.replace(/\s+/g, "");
              const tasksData = tasksState[statusKey] || [];

              // build visible tasks from the local tasksData (that can be reordered)
              const visibleTasks = buildVisibleTasks(tasksData);

              // per-status drag end handler that updates tasksState for this statusKey
              const handleDragEnd = ({ active, over, delta }) => {
                if (!over || String(active.id) === String(over.id)) return;

                // find indexes inside the visibleTasks array
                const activeIndex = visibleTasks.findIndex((t) => String(t.id) === String(active.id));
                const overIndex = visibleTasks.findIndex((t) => String(t.id) === String(over.id));

                // if either index is -1, bail (safety)
                if (activeIndex === -1 || overIndex === -1) return;

                // Reorder the visible tasks (note: visibleTasks is a flattened view; to keep this example simple
                // we reorder the top-level array tasksData by mapping through visibleTasks indexes.)
                // For simpler immediate UX we reorder the top-level tasksData array from which visibleTasks were derived
                // by using arrayMove on the flattened visibleTasks and then replacing tasksData with the flattened result.
                // Depending on your actual hierarchical model you might want a more careful tree reassign.

                // Create a flat copy of visibleTasks, move items:
                const newVisible = arrayMove([...visibleTasks], activeIndex, overIndex);

                // Now reconstruct a new tasksData array by:
                // - replacing the tasks in tasksData that are present in visibleTasks order,
                // - keeping other tasks (e.g. tasks that are children of collapsed parents) in place.
                // Simpler approach: create a map of id -> task from tasksData, then build new tasksData
                // that places visible items in the new order and then appends any remaining tasks.
                const idToTask = new Map(tasksData.map((t) => [String(t.id), { ...t }]));
                const reorderedVisibleIds = newVisible.map((t) => String(t.id));

                // Build reordered tasksData: put visible items in new order first,
                // then add any tasks not in visible list in their original order.
                const remaining = tasksData.filter((t) => !reorderedVisibleIds.includes(String(t.id)));
                const reordered = [...newVisible.map((t) => idToTask.get(String(t.id))), ...remaining];

                // handle indenting via delta.x (optional - mimic original intent)
                const indentThreshold = 25;
                const movedIndexInReordered = reordered.findIndex((t) => String(t.id) === String(active.id));
                if (movedIndexInReordered !== -1) {
                  const movedTask = { ...reordered[movedIndexInReordered] };
                  if (delta && delta.x > indentThreshold) {
                    // make the moved task child of the previous item if possible
                    const above = reordered[movedIndexInReordered - 1];
                    if (above && String(above.id) !== String(movedTask.id)) {
                      movedTask.parent = above.id;
                    }
                  } else if (delta && delta.x < -indentThreshold) {
                    // outdent -> make parent null
                    movedTask.parent = null;
                  }
                  reordered[movedIndexInReordered] = movedTask;
                }

                // Update tasksState for this status
                setTasksState((prev) => ({ ...prev, [statusKey]: reordered }));
              };

              return (
                <AccordionTab
                  key={task_status}
                  header={
                    <>
                      <span className="task-status-title">{task_status}</span>
                      <span className="font-bold white-space-nowrap">
                        {count}
                        {/* <button type="button" className="btn btn-sm ms-2">
                        <i className="ri-add-line"></i> Add task
                      </button> */}
                      </span>
                    </>
                  }
                  className={`box-gray ${getStatusClass(task_status)}`}
                >
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={visibleTasks.map((t) => String(t.id))} strategy={verticalListSortingStrategy}>
                      <table className="table draggable-table">
                        <thead>
                          {/* <tr className="addtask-top-btn">
                          <th>
                            <button type="button" className="btn" onClick={() => setShowTaskForm(task_status)}>
                              <i className="ri-add-line"></i>
                            </button>
                          </th>
                        </tr> */}
                          <tr>
                            <th>
                              <div className="form-check">
                                {/* <input type="checkbox" className="form-check-input" /> */}
                                <label className="form-check-label"></label>Name
                              </div>
                            </th>
                            <th>Assignee</th>
                            <th>Due date</th>
                            <th>Priority</th>
                            <th>
                              <i className="ri-add-circle-line"></i>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleTasks.map((task) => {
                            const isParent = tasksData.some((t) => t.parent === task.id);
                            const subtaskCount = task.sub_tasks ? task.sub_tasks.length : 0;
                            return (
                              <Task
                                key={task.id}
                                task={task}
                                depth={task.depth}
                                isParent={isParent}
                                isCollapsed={collapsed[task.id] ?? true}
                                handleDelete={handleDelete}
                                onToggle={toggleCollapse}
                                subtaskCount={subtaskCount}
                                onClickTaskDetail={onClickTaskDetail}
                                task_status={task_status}
                                onUpdate={(updatedFields) => handleUpdate(task.id, updatedFields, statusKey)}
                                statusOptions={taskList.statusList.map((s) => s.task_status)}
                              />
                            );
                          })}
                          {showTaskForm === task_status && (
                            <tr>
                              <td colSpan={5}>
                                <form className="task-add-form" onSubmit={(e) => onHandleSubmit(e, task_status)}>
                                  <i className="ri-donut-chart-fill"></i>
                                  <div className="form-group">
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Enter task name"
                                      name="title"
                                      autoFocus
                                      value={modifiedTaskDetails.title || ""}
                                      onChange={(e) => {
                                        onHandleEvent(e, setModifiedTaskDetails);
                                      }}
                                    />
                                  </div>
                                  <div className="btn-stack">
                                    <Calendar
                                      className="task-table-calendar"
                                      value={modifiedTaskDetails.dueDate || ""}
                                      name="dueDate"
                                      onChange={(e) => {
                                        // setDates(e.value);
                                        setModifiedTaskDetails((prev) => ({
                                          ...prev,
                                          dueDate: e.value,
                                        }));
                                      }}
                                      readOnlyInput
                                      dateFormat="M dd yy"
                                      showButtonBar
                                      showIcon
                                    />

                                    {/* <Calendar className="task-table-calendar" dateFormat="M dd, yy" showButtonBar showIcon /> */}
                                    <i className="ri-user-add-line"></i>
                                    <i className="ri-flag-2-line"></i>
                                  </div>
                                  <div>
                                    <button
                                      type="button"
                                      className="btn btn-exit"
                                      onClick={() => {
                                        // setDates(null);
                                        setShowTaskForm(null);
                                        setModifiedTaskDetails({});
                                      }}
                                    >
                                      Cancel
                                    </button>
                                    <button type="submit" className="btn btn-save" disabled={!modifiedTaskDetails.title || modifiedTaskDetails.title.trim() === ""}>
                                      Save
                                    </button>
                                  </div>
                                </form>
                              </td>
                            </tr>
                          )}
                          {showTaskForm !== task_status && (
                            <tr>
                              <td>
                                <button
                                  type="button"
                                  className="btn add-btn"
                                  onClick={() => {
                                    // setDates(null);
                                    setShowTaskForm(task_status);
                                  }}
                                >
                                  <i className="ri-add-line"></i> Add task
                                </button>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </SortableContext>
                  </DndContext>
                </AccordionTab>
              );
            })}
          </Accordion>
          <div className="mt-3">
            <button type="button" className="btn btn-solid">
              <i className="ri-add-line"></i> Add Group
            </button>
          </div>
        </div>
      ) : (
        <div className="loading-hover">
          <div className="property-loader preloader">
            <div className="loading">
              <div className="loading__letter">L</div>
              <div className="loading__letter">o</div>
              <div className="loading__letter">a</div>
              <div className="loading__letter">d</div>
              <div className="loading__letter">i</div>
              <div className="loading__letter">n</div>
              <div className="loading__letter">g</div>
              <div className="loading__letter">.</div>
              <div className="loading__letter">.</div>
              <div className="loading__letter">.</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
